import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type { Study, StudyCreate, StudyKind, StudyUpdate } from "../types";

const KEY = ["studies"] as const;

export interface StudyFilters {
  disease_area_id?: string;
  kind?: StudyKind;
}

function buildQuery(filters?: StudyFilters): string {
  if (!filters) return "";
  const params = new URLSearchParams();
  if (filters.disease_area_id) params.set("disease_area_id", filters.disease_area_id);
  if (filters.kind) params.set("kind", filters.kind);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function useStudies(filters?: StudyFilters) {
  return useQuery<Study[]>({
    queryKey: [...KEY, filters ?? {}],
    queryFn: () => apiRequest(`/api/studies${buildQuery(filters)}`),
  });
}

export function useStudy(id: string | undefined) {
  return useQuery<Study>({
    queryKey: [...KEY, id],
    queryFn: () => apiRequest(`/api/studies/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateStudy() {
  const qc = useQueryClient();
  return useMutation<Study, Error, StudyCreate>({
    mutationFn: (payload) =>
      apiRequest("/api/studies", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateStudy(id: string) {
  const qc = useQueryClient();
  return useMutation<Study, Error, StudyUpdate>({
    mutationFn: (payload) =>
      apiRequest(`/api/studies/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: (s) => {
      qc.setQueryData([...KEY, id], s);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteStudy() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiRequest(`/api/studies/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
